/* ==========================================================================
   My Teacher Says — stamp text layout rule

   The ONE rule for setting a customer's name on a personalised stamp, shared by
   the live preview on the product page (browser) and the sheet builder that
   makes the print files (Node, tools/print-sheets). Keeping it in a single file
   is what guarantees the preview and the printed stamp agree.

   Units are "design units": every stamp design is drawn on a 1080-unit square
   artboard (1 mm ≈ 43.8 units on a 23 mm stamp). A design has two text arcs set
   in the same font at the same size — one over the top (baseline on the inside,
   letters pointing out) and one under the bottom (baseline on the outside,
   letters pointing in). Either arc may carry the {name}.

   Liam's rule (2 Sept 2026): the two texts never differ in size. A long name
   first tightens its tracking, then travels further round the circle towards
   the other text (keeping a visible gap), and only then do BOTH texts shrink
   together. Text never runs into the icon: each design says how far round each
   arc may go before it would (spanTop / spanBottom, measured from the artwork).

   Plain ES5, no dependencies, so the same file runs in an old browser and in
   Node (module.exports guard at the bottom).
   ========================================================================== */
var MTS_STAMP_LAYOUT = {
  /* Defaults; every design overrides these from its own artwork. */
  R_TOP: 250,          /* baseline radius of the top arc */
  R_BOTTOM: 350,       /* baseline radius of the bottom arc (outer edge) */
  FONT_MAX: 138,       /* the size the phrases are drawn at in the Illustrator master */
  TRACK_START: 0.02,   /* em, comfortable */
  TRACK_MIN: -0.01,    /* em, the tightest we go before travelling round */
  GAP_MAX_DEG: 34,     /* clear space between the two arcs at each side, comfortable */
  GAP_MIN_DEG: 18,     /* ...and the closest they may come for a long name */
  MAX_CHARS: 24,

  /* "{name} today" -> "Mrs Parry today". */
  arcText: function (name, template) {
    return String(template || '{name}').replace('{name}', name);
  },

  /* "says" handling: the customer types only their name; the design decides the
     suffix; the customer may switch it off; typing it themselves never doubles it. */
  compose: function (name, suffix, wantSuffix) {
    var clean = String(name || '').replace(/\s+/g, ' ').trim();
    if (!suffix) return clean;
    var re = new RegExp('\\s+' + suffix + '$', 'i');
    if (re.test(clean)) return clean;
    return wantSuffix && clean ? clean + ' ' + suffix : clean;
  },

  /* measure(text, fontSize) -> advance width in design units, without tracking.
     limits: { rTop, rBottom, spanTop, spanBottom, fontMax } from the design.
     Returns { fontSize, tracking, gapDeg, topDeg, bottomDeg, fits }. */
  fitPair: function (topText, bottomText, measure, limits) {
    var self = this;
    limits = limits || {};
    var rTop = limits.rTop || this.R_TOP;
    var rBottom = limits.rBottom || this.R_BOTTOM;
    var spanTop = limits.spanTop || 360;
    var spanBottom = limits.spanBottom || 360;
    var FONT_MAX = limits.fontMax || this.FONT_MAX;
    var FONT_MIN = Math.round(FONT_MAX * 0.62);

    /* Tracking is specified where the eye reads it, at the x-height line of the ring
       (rMid), and converted to a baseline value per arc. The top text's baseline is
       inside the ring, so its letters fan open towards the reader; the bottom text's
       baseline is outside, so its letters squeeze together. Scaling each arc's average
       advance to the same arc length at rMid balances the two (Liam, 2 Sept 2026). */
    var rMid = (rTop + rBottom) / 2;
    /* The spacing the eye reads is the gap plus some of the letter body, taken here as half
       the average advance (the whole advance over-corrects, the side-bearings alone barely
       register). */
    var kTop = (topText.length ? measure(topText, 100) / 100 / topText.length : 0.5) / 2;
    var kBot = (bottomText.length ? measure(bottomText, 100) / 100 / bottomText.length : 0.5) / 2;
    var trackTop = function (tr) { return (kTop + tr) * (rTop / rMid) - kTop; };
    var trackBottom = function (tr) { return (kBot + tr) * (rBottom / rMid) - kBot; };
    var spans = function (fs, tr) {
      var wt = measure(topText, fs) + trackTop(tr) * fs * Math.max(topText.length - 1, 0);
      var wb = measure(bottomText, fs) + trackBottom(tr) * fs * Math.max(bottomText.length - 1, 0);
      return { top: (wt / rTop) * 180 / Math.PI, bottom: (wb / rBottom) * 180 / Math.PI };
    };
    /* The smallest slack across the three constraints, as a gap in degrees: the gap
       between the two texts, and how far each text is inside its icon-free span. */
    var roomAt = function (s) {
      return Math.min(
        (360 - s.top - s.bottom) / 2,
        (spanTop - s.top) / 2 + self.GAP_MIN_DEG,
        (spanBottom - s.bottom) / 2 + self.GAP_MIN_DEG
      );
    };
    var result = function (fs, tr, s, fits) {
      return {
        fontSize: fs, tracking: tr,
        trackingTop: +trackTop(tr).toFixed(4), trackingBottom: +trackBottom(tr).toFixed(4),
        gapDeg: (360 - s.top - s.bottom) / 2, topDeg: s.top, bottomDeg: s.bottom, fits: fits
      };
    };

    var fs = FONT_MAX, tr = this.TRACK_START, s = spans(fs, tr);
    if (roomAt(s) >= this.GAP_MAX_DEG) return result(fs, tr, s, true);

    /* 1. Tighten tracking, keeping the comfortable gap if that alone does it. */
    tr = this.TRACK_MIN; s = spans(fs, tr);
    if (roomAt(s) >= this.GAP_MAX_DEG) {
      var lo = this.TRACK_MIN, hi = this.TRACK_START;
      for (var i = 0; i < 12; i++) {
        var mid = (lo + hi) / 2;
        if (roomAt(spans(fs, mid)) >= this.GAP_MAX_DEG) lo = mid; else hi = mid;
      }
      return result(fs, lo, spans(fs, lo), true);
    }
    /* 2. Let the text travel further round, down to the minimum gap. */
    if (roomAt(s) >= this.GAP_MIN_DEG) return result(fs, tr, s, true);

    /* 3. Shrink both texts together until every constraint is met. Spans scale
       linearly with size, so bisect on the size. */
    var loFs = FONT_MIN, hiFs = FONT_MAX;
    if (roomAt(spans(loFs, tr)) < this.GAP_MIN_DEG) return result(FONT_MIN, tr, spans(FONT_MIN, tr), false);
    for (var j = 0; j < 14; j++) {
      var midFs = (loFs + hiFs) / 2;
      if (roomAt(spans(midFs, tr)) >= this.GAP_MIN_DEG) loFs = midFs; else hiFs = midFs;
    }
    fs = Math.floor(loFs);
    return result(fs, tr, spans(fs, tr), true);
  },

  /* One text on its own path (the Library stamps: the name on an oval, or a name in the
     bottom arc while the phrase above stays fixed). Only this text changes: tracking
     tightens first, then the size comes down until the text spans no more than spanDeg
     of the path. r is the path's radius (mean radius for an oval).
     Returns { fontSize, tracking, spanDeg, fits }. */
  fitSingle: function (text, measure, o) {
    var r = o.r, spanMax = o.spanMax || 150, FONT_MAX = o.fontMax, FONT_MIN = Math.round(FONT_MAX * 0.5);
    var trStart = o.trackStart == null ? this.TRACK_START : o.trackStart;
    var trMin = o.trackMin == null ? this.TRACK_MIN : o.trackMin;
    var span = function (fs, tr) { return ((measure(text, fs) + tr * fs * Math.max(text.length - 1, 0)) / r) * 180 / Math.PI; };
    var result = function (fs, tr, fits) { return { fontSize: fs, tracking: tr, spanDeg: span(fs, tr), fits: fits }; };
    var fs = FONT_MAX, tr = trStart;
    if (span(fs, tr) <= spanMax) return result(fs, tr, true);
    tr = trMin;
    if (span(fs, tr) <= spanMax) {
      var lo = trMin, hi = trStart;
      for (var i = 0; i < 12; i++) { var mid = (lo + hi) / 2; if (span(fs, mid) <= spanMax) lo = mid; else hi = mid; }
      return result(fs, lo, true);
    }
    fs = Math.floor(FONT_MAX * spanMax / span(FONT_MAX, tr));
    if (fs >= FONT_MIN) return result(fs, tr, true);
    return result(FONT_MIN, tr, false);
  },

  /* Straight lines on a rectangular stamp (the fabric name stamp): every line at one size,
     the widest must fit maxW (design units). Tracking tightens first, then the size comes
     down to fontMin (defaults to 0.55 x fontMax). o: { maxW, fontMax, fontMin, trackStart,
     trackMin }. Returns { fontSize, tracking, width, fits }. */
  fitLines: function (lines, measure, o) {
    var texts = [];
    for (var i = 0; i < lines.length; i++) if (lines[i]) texts.push(String(lines[i]));
    if (!texts.length) texts = [''];
    var FONT_MAX = o.fontMax, FONT_MIN = o.fontMin || Math.round(FONT_MAX * 0.55);
    var trStart = o.trackStart == null ? this.TRACK_START : o.trackStart;
    var trMin = o.trackMin == null ? this.TRACK_MIN : o.trackMin;
    var widest = function (fs, tr) {
      var w = 0;
      for (var k = 0; k < texts.length; k++) {
        var t = texts[k];
        w = Math.max(w, measure(t, fs) + tr * fs * Math.max(t.length - 1, 0));
      }
      return w;
    };
    var result = function (fs, tr, fits) { return { fontSize: fs, tracking: tr, width: widest(fs, tr), fits: fits }; };
    var fs = FONT_MAX, tr = trStart;
    if (widest(fs, tr) <= o.maxW) return result(fs, tr, true);
    tr = trMin;
    if (widest(fs, tr) <= o.maxW) {
      var lo = trMin, hi = trStart;
      for (var j = 0; j < 12; j++) { var mid = (lo + hi) / 2; if (widest(fs, mid) <= o.maxW) lo = mid; else hi = mid; }
      return result(fs, lo, true);
    }
    /* width scales with size */
    fs = Math.floor(FONT_MAX * o.maxW / widest(FONT_MAX, tr));
    if (fs >= FONT_MIN) return result(fs, tr, true);
    return result(FONT_MIN, tr, false);
  },

  /* A full oval, starting at the bottom and running clockwise, so text centred with
     startOffset 50% sits at the top and reads left to right. */
  ellipsePath: function (rx, ry, cx, cy) {
    return 'M ' + cx + ' ' + (cy + ry) + ' A ' + rx + ' ' + ry + ' 0 1 1 ' + cx + ' ' + (cy - ry) + ' A ' + rx + ' ' + ry + ' 0 1 1 ' + cx + ' ' + (cy + ry);
  },

  /* SVG path data for the two arcs, as full circles so long text never clips.
     Top: starts at 6 o'clock and runs clockwise, so its midpoint (startOffset 50%)
     is 12 o'clock. Bottom: starts at 12 o'clock and runs anticlockwise, so its
     midpoint is 6 o'clock and it reads left to right with letters pointing in. */
  arcPath: function (slot, r, cx, cy) {
    cx = cx == null ? 540 : cx; cy = cy == null ? 540 : cy;
    if (slot === 'bottom') {
      return 'M ' + cx + ' ' + (cy - r) + ' A ' + r + ' ' + r + ' 0 1 0 ' + cx + ' ' + (cy + r) + ' A ' + r + ' ' + r + ' 0 1 0 ' + cx + ' ' + (cy - r);
    }
    return 'M ' + cx + ' ' + (cy + r) + ' A ' + r + ' ' + r + ' 0 1 1 ' + cx + ' ' + (cy - r) + ' A ' + r + ' ' + r + ' 0 1 1 ' + cx + ' ' + (cy + r);
  }
};

if (typeof module !== 'undefined' && module.exports) module.exports = MTS_STAMP_LAYOUT;
